import React, { useState, useEffect } from "react";
import withClinic from "../../hooks/with_clinic";
import { ApolloResult, Clinic, SaleReport, SaleReportResponse, SaleGroupReport, SaleGroupReportResponse } from "../../store";
import { Row, Col, Typography, Input, Table, Button, Tabs } from "antd";
import DateRange from "../../components/date_range";
import { Form } from "antd";
import { format, startOfDay, endOfDay } from "date-fns";
import { useQuery, useApolloClient } from "@apollo/client";
import LoadingSpinner from "../../components/loading_indicator";
import HorizontalScrollContainer from "../../components/horizontal_scroll_container";
import Error from "../../components/apollo_error";
import { sale_report, getSaleReportVar, sale_group_report, getSaleGroupReportVar } from "../../graphql/sale_report";
import { formatMoney, getCurrency, formatPhoneNumber } from "../../helpers/utils";
import { SearchOutlined, LeftOutlined, RightOutlined, FileExcelFilled } from "@ant-design/icons";
import * as XLSX from "xlsx";
import FileSaver from "file-saver";

interface P {
  currentClinic?: Clinic;
}

const DailySaleDetailReportPage = (props: P) => {
  const clinicCode = props.currentClinic?.code || "";
  const client = useApolloClient();
  const [activeTab, setActiveTab] = useState<string>("detail");
  const [searchText, setSearchText] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState<number>(50);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [exporting, setExporting] = useState(false);
  const [ranges, setRanges] = useState({
    startDate: startOfDay(new Date()),
    endDate: endOfDay(new Date()),
  });
  const [form] = Form.useForm();

  useEffect(() => {
    setCurrentPage(1);
  }, [ranges.startDate, ranges.endDate, pageSize, activeTab]);

  const skip = (currentPage - 1) * pageSize;
  const detailVariables = getSaleReportVar({
    clinic_code: clinicCode,
    from_date: ranges.startDate,
    to_date: ranges.endDate,
    take: pageSize,
    skip: skip,
  });

  const saleBySalespersonVariables = getSaleGroupReportVar({
    clinic_code: clinicCode,
    from_date: ranges.startDate,
    to_date: ranges.endDate,
    report_type: "SALE_BY_SALESPERSON",
    take: pageSize,
    skip: skip,
  });

  const customerBySalespersonVariables = getSaleGroupReportVar({
    clinic_code: clinicCode,
    from_date: ranges.startDate,
    to_date: ranges.endDate,
    report_type: "CUSTOMER_BY_SALESPERSON",
    take: pageSize,
    skip: skip,
  });

  const { loading: detailLoading, data: detailData, error: detailError } = useQuery<
    ApolloResult<"saleReport", SaleReportResponse>
  >(sale_report, {
    variables: detailVariables,
    skip: !clinicCode,
    fetchPolicy: "cache-and-network",
  });

  const { loading: saleBySalespersonLoading, data: saleBySalespersonData, error: saleBySalespersonError } = useQuery<
    ApolloResult<"saleGroupReport", SaleGroupReportResponse>
  >(sale_group_report, {
    variables: saleBySalespersonVariables,
    skip: !clinicCode,
    fetchPolicy: "cache-and-network",
  });

  const { loading: customerBySalespersonLoading, data: customerBySalespersonData, error: customerBySalespersonError } = useQuery<
    ApolloResult<"saleGroupReport", SaleGroupReportResponse>
  >(sale_group_report, {
    variables: customerBySalespersonVariables,
    skip: !clinicCode,
    fetchPolicy: "cache-and-network",
  });

  const loading = activeTab === "detail" 
    ? detailLoading 
    : activeTab === "saleBySalesperson" 
    ? saleBySalespersonLoading 
    : customerBySalespersonLoading;
  const error = activeTab === "detail" 
    ? detailError 
    : activeTab === "saleBySalesperson" 
    ? saleBySalespersonError 
    : customerBySalespersonError;

  if (loading) return <LoadingSpinner />;
  if (error) return <Error error={error} />;

  const onDateChange = (startDate: Date, endDate: Date) => {
    setRanges({ startDate, endDate });
  };

  // Detail report data
  const saleReportResponse = detailData?.saleReport;
  const saleReportData = saleReportResponse?.data || [];
  const detailTotal = saleReportResponse?.total || 0;

  // Sale by Salesperson report data
  const saleBySalespersonResponse = saleBySalespersonData?.saleGroupReport;
  const saleBySalespersonDataList = saleBySalespersonResponse?.data || [];
  const saleBySalespersonTotal = saleBySalespersonResponse?.total || 0;

  // Customer by Salesperson report data
  const customerBySalespersonResponse = customerBySalespersonData?.saleGroupReport;
  const customerBySalespersonDataList = customerBySalespersonResponse?.data || [];
  const customerBySalespersonTotal = customerBySalespersonResponse?.total || 0;

  const handleExport = async () => {
    const fileName =
      `sale_report_all_` +
      format(ranges.startDate, "yyyy-MMM-dd") +
      "_" +
      format(ranges.endDate, "yyyy-MMM-dd");
    
    setExporting(true);

    try {
      const sheets: { name: string; data: any[] }[] = [];
      const EXPORT_BATCH = 500;

      // Export Detail Report
      let detailList: any[] = [];
      let rolling = true;
      let skip = 0;

      while (rolling) {
        const variables = getSaleReportVar({
          clinic_code: clinicCode,
          from_date: ranges.startDate,
          to_date: ranges.endDate,
          take: EXPORT_BATCH,
          skip,
        });
        const result = await client.query<
          ApolloResult<"saleReport", SaleReportResponse>
        >({
          query: sale_report,
          variables,
          fetchPolicy: "network-only",
        });
        const data = result.data?.saleReport?.data || [];
        if (data.length === 0) {
          rolling = false;
        } else {
          detailList = detailList.concat(data);
          skip += data.length;
          rolling = data.length === EXPORT_BATCH;
        }
      }

      const detailData: any[] = [];
      for (const item of detailList) {
        detailData.push({
          "Visit Date": item.visit_date
            ? format(new Date(item.visit_date), "yyyy-MMM-dd")
            : "-",
          "Customer Name": item.customer_name || "-",
          "Customer Phone":
            formatPhoneNumber(item.customer_phone, props.currentClinic) || "-",
          Seller: item.seller_name || "-",
          Services: item.services || "-",
          Practitioner: item.practitioner || "-",
          Helper: item.helper || "-",
          "Payment Amount": item.payment_amount ?? 0,
          "Payment Method": item.payment_method || "-",
          "Payment Note": item.payment_note || "-",
          "New Customer":
            item.is_new_customer_in_clinic === "true" ||
            item.is_new_customer_in_clinic === "1"
              ? "Yes"
              : "No",
        });
      }
      sheets.push({
        name: "Detail Report",
        data: detailData,
      });

      // Export Sale by Seller
      let saleBySellerList: any[] = [];
      rolling = true;
      skip = 0;

      while (rolling) {
        const variables = getSaleGroupReportVar({
          clinic_code: clinicCode,
          from_date: ranges.startDate,
          to_date: ranges.endDate,
          report_type: "SALE_BY_SALESPERSON",
          take: EXPORT_BATCH,
          skip,
        });
        const result = await client.query<
          ApolloResult<"saleGroupReport", SaleGroupReportResponse>
        >({
          query: sale_group_report,
          variables,
          fetchPolicy: "network-only",
        });
        const data = result.data?.saleGroupReport?.data || [];
        if (data.length === 0) {
          rolling = false;
        } else {
          saleBySellerList = saleBySellerList.concat(data);
          skip += data.length;
          rolling = data.length === EXPORT_BATCH;
        }
      }

      const saleBySellerData: any[] = [];
      for (const item of saleBySellerList) {
        saleBySellerData.push({
          Seller: item.seller_name || "-",
          "Total Payment Amount": item.payment_amount ?? 0,
        });
      }
      sheets.push({
        name: "Sale by Seller",
        data: saleBySellerData,
      });

      // Export Customer by Seller
      let customerBySellerList: any[] = [];
      rolling = true;
      skip = 0;

      while (rolling) {
        const variables = getSaleGroupReportVar({
          clinic_code: clinicCode,
          from_date: ranges.startDate,
          to_date: ranges.endDate,
          report_type: "CUSTOMER_BY_SALESPERSON",
          take: EXPORT_BATCH,
          skip,
        });
        const result = await client.query<
          ApolloResult<"saleGroupReport", SaleGroupReportResponse>
        >({
          query: sale_group_report,
          variables,
          fetchPolicy: "network-only",
        });
        const data = result.data?.saleGroupReport?.data || [];
        if (data.length === 0) {
          rolling = false;
        } else {
          customerBySellerList = customerBySellerList.concat(data);
          skip += data.length;
          rolling = data.length === EXPORT_BATCH;
        }
      }

      const customerBySellerData: any[] = [];
      for (const item of customerBySellerList) {
        customerBySellerData.push({
          "Customer Name": item.customer_name || "-",
          "Customer Phone":
            formatPhoneNumber(item.customer_phone, props.currentClinic) || "-",
          Seller: item.seller_name || "-",
          "Total Payment Amount": item.payment_amount ?? 0,
        });
      }
      sheets.push({
        name: "Customer by Seller",
        data: customerBySellerData,
      });

      // Create multi-sheet Excel file
      const fileType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      const fileExtension = ".xlsx";
      const wb: { Sheets: { [key: string]: any }; SheetNames: string[] } = {
        Sheets: {},
        SheetNames: [],
      };

      for (const sheet of sheets) {
        const ws = XLSX.utils.json_to_sheet(sheet.data);
        wb.Sheets[sheet.name] = ws;
        wb.SheetNames.push(sheet.name);
      }

      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const data = new Blob([excelBuffer], { type: fileType });
      FileSaver.saveAs(data, fileName + fileExtension);
    } catch (error) {
      console.error("Export error:", error);
    } finally {
      setExporting(false);
    }
  };

  const total = activeTab === "detail" 
    ? detailTotal 
    : activeTab === "saleBySalesperson" 
    ? saleBySalespersonTotal 
    : customerBySalespersonTotal;
  const totalPages = Math.ceil(total / pageSize);
  const hasMoreData = currentPage < totalPages;

  const handleNextPage = () => {
    if (hasMoreData) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const filteredDetailData = saleReportData.filter((item) => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    return (
      item.customer_name?.toLowerCase().includes(searchLower) ||
      item.customer_phone?.includes(searchText) ||
      item.seller_name?.toLowerCase().includes(searchLower) ||
      item.services?.toLowerCase().includes(searchLower) ||
      item.practitioner?.toLowerCase().includes(searchLower)
    );
  });

  const filteredSaleBySalespersonData = saleBySalespersonDataList.filter((item) => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    return (
      item.seller_name?.toLowerCase().includes(searchLower)
    );
  });

  const filteredCustomerBySalespersonData = customerBySalespersonDataList.filter((item) => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    return (
      item.customer_name?.toLowerCase().includes(searchLower) ||
      item.customer_phone?.includes(searchText) ||
      item.seller_name?.toLowerCase().includes(searchLower)
    );
  });

  const detailColumns: any[] = [
    {
      title: "Visit Date",
      dataIndex: "visit_date",
      key: "visit_date",
      align: "center",
      width: 120,
      sorter: (a: SaleReport, b: SaleReport) => {
        const dateA = a.visit_date ? new Date(a.visit_date).getTime() : 0;
        const dateB = b.visit_date ? new Date(b.visit_date).getTime() : 0;
        return dateA - dateB;
      },
      render: (date: Date | undefined) => {
        if (!date) return "-";
        return format(new Date(date), "dd MMM yyyy");
      },
    },
    {
      title: "Customer",
      key: "customer",
      width: 200,
      render: (record: SaleReport) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.customer_name || "-"}</div>
          <div style={{ fontSize: "12px", color: "#888" }}>
            {formatPhoneNumber(record.customer_phone, props.currentClinic) || "-"}
          </div>
        </div>
      ),
    },
    {
      title: "Seller",
      dataIndex: "seller_name",
      key: "seller_name",
      width: 150,
      render: (text: string | undefined) => text || "-",
    },
    {
      title: "Services",
      dataIndex: "services",
      key: "services",
      width: 200,
      render: (text: string | undefined) => text || "-",
    },
    {
      title: "Practitioner",
      dataIndex: "practitioner",
      key: "practitioner",
      width: 150,
      render: (text: string | undefined) => text || "-",
    },
    {
      title: "Helper",
      dataIndex: "helper",
      key: "helper",
      width: 120,
      render: (text: string | undefined) => text || "-",
    },
    {
      title: "Payment Amount",
      dataIndex: "payment_amount",
      key: "payment_amount",
      align: "right",
      width: 150,
      sorter: (a: SaleReport, b: SaleReport) => {
        const amountA = a.payment_amount || 0;
        const amountB = b.payment_amount || 0;
        return amountA - amountB;
      },
      render: (amount: number | undefined) => {
        if (amount === undefined || amount === null) return "-";
        return `${formatMoney(amount)} ${getCurrency(props.currentClinic)}`;
      },
    },
    {
      title: "Payment Method",
      dataIndex: "payment_method",
      key: "payment_method",
      width: 150,
      render: (text: string | undefined) => text || "-",
    },
    {
      title: "Payment Note",
      dataIndex: "payment_note",
      key: "payment_note",
      width: 200,
      render: (text: string | undefined) => text || "-",
    },
    {
      title: "New Customer",
      dataIndex: "is_new_customer_in_clinic",
      key: "is_new_customer_in_clinic",
      align: "center",
      width: 120,
      render: (text: string | undefined) => {
        if (!text) return "-";
        return text === "true" || text === "1" ? "Yes" : "No";
      },
    },
  ];

  const saleBySalespersonColumns: any[] = [
    {
      title: "Seller",
      dataIndex: "seller_name",
      key: "seller_name",
      width: 200,
      render: (text: string | undefined) => text || "-",
    },
    {
      title: "Total Payment Amount",
      dataIndex: "payment_amount",
      key: "payment_amount",
      align: "right",
      width: 200,
      sorter: (a: SaleGroupReport, b: SaleGroupReport) => {
        const amountA = a.payment_amount || 0;
        const amountB = b.payment_amount || 0;
        return amountA - amountB;
      },
      render: (amount: number | undefined) => {
        if (amount === undefined || amount === null) return "-";
        return `${formatMoney(amount)} ${getCurrency(props.currentClinic)}`;
      },
    },
  ];

  const customerBySalespersonColumns: any[] = [
    {
      title: "Customer",
      key: "customer",
      width: 200,
      render: (record: SaleGroupReport) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.customer_name || "-"}</div>
          <div style={{ fontSize: "12px", color: "#888" }}>
            {formatPhoneNumber(record.customer_phone, props.currentClinic) || "-"}
          </div>
        </div>
      ),
    },
    {
      title: "Seller",
      dataIndex: "seller_name",
      key: "seller_name",
      width: 200,
      render: (text: string | undefined) => text || "-",
    },
    {
      title: "Total Payment Amount",
      dataIndex: "payment_amount",
      key: "payment_amount",
      align: "right",
      width: 200,
      sorter: (a: SaleGroupReport, b: SaleGroupReport) => {
        const amountA = a.payment_amount || 0;
        const amountB = b.payment_amount || 0;
        return amountA - amountB;
      },
      render: (amount: number | undefined) => {
        if (amount === undefined || amount === null) return "-";
        return `${formatMoney(amount)} ${getCurrency(props.currentClinic)}`;
      },
    },
  ];

  const control = (
    <Row style={{ marginTop: -18 }} gutter={16}>
      <Col flex={4}>
        <Row>
          <Col span={8}>
            <Typography.Title level={2}>Daily Sale Report</Typography.Title>
          </Col>
          <Col span={8} offset={8}>
            <Input
              placeholder={
                activeTab === "detail" 
                  ? "Search customer, seller, services..." 
                  : activeTab === "saleBySalesperson"
                  ? "Search seller..."
                  : "Search customer, seller..."
              }
              prefix={<SearchOutlined />}
              allowClear
              size="middle"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.target.value || null)}
            />
          </Col>
        </Row>
      </Col>
      <Col flex={0}>
        <DateRange
          startDate={ranges.startDate}
          endDate={ranges.endDate}
          onSelect={onDateChange}
        />
      </Col>
      <Col span={2}>
        <Button
          loading={exporting}
          icon={<FileExcelFilled />}
          onClick={handleExport}
        >
          Export
        </Button>
      </Col>
    </Row>
  );

  const detailTable = (
    <Row style={{ marginTop: 8 }}>
      <Col span={24}>
        <Form form={form} component={false}>
          <HorizontalScrollContainer>
            <Table
              loading={loading}
              size="middle"
              showHeader={filteredDetailData.length > 0}
              dataSource={filteredDetailData.map((item, index) => ({
                key: `${item.customer_id}-${item.visit_date}-${index}`,
                ...item,
              }))}
              columns={detailColumns}
              rowClassName="editable-row"
              pagination={{
              position: ["bottomRight"],
              pageSize: pageSize,
              current: currentPage,
              total: total,
              showSizeChanger: true,
              pageSizeOptions: ["20", "50", "100", "200"],
              showTotal: (total: number, range: [number, number]) => 
                `Showing ${range[0]}-${range[1]} of ${total} records`,
              onShowSizeChange: (_current: number, size: number) => {
                setPageSize(size);
                setCurrentPage(1);
              },
              showQuickJumper: true,
              showLessItems: false,
              hideOnSinglePage: false,
              onChange: (page: number) => {
                setCurrentPage(page);
              },
              itemRender: (_page: number, type: string, originalElement: React.ReactNode) => {
                if (type === "prev") {
                  return (
                    <Button
                      icon={<LeftOutlined />}
                      disabled={currentPage === 1}
                      onClick={handlePrevPage}
                    >
                      Previous
                    </Button>
                  );
                }
                if (type === "next") {
                  return (
                    <Button
                      icon={<RightOutlined />}
                      disabled={!hasMoreData}
                      onClick={handleNextPage}
                    >
                      Next
                    </Button>
                  );
                }
                return originalElement;
              },
            }}
            scroll={{ x: 1500 }}
          />
          </HorizontalScrollContainer>
        </Form>
      </Col>
    </Row>
  );

  const saleBySalespersonTable = (
    <Row style={{ marginTop: 8 }}>
      <Col span={24}>
        <Form form={form} component={false}>
          <HorizontalScrollContainer>
            <Table
              loading={loading}
              size="middle"
              showHeader={filteredSaleBySalespersonData.length > 0}
              dataSource={filteredSaleBySalespersonData.map((item, index) => ({
                key: `${item.seller_id}-${index}`,
                ...item,
              }))}
              columns={saleBySalespersonColumns}
              rowClassName="editable-row"
              pagination={{
              position: ["bottomRight"],
              pageSize: pageSize,
              current: currentPage,
              total: total,
              showSizeChanger: true,
              pageSizeOptions: ["20", "50", "100", "200"],
              showTotal: (total: number, range: [number, number]) => 
                `Showing ${range[0]}-${range[1]} of ${total} records`,
              onShowSizeChange: (_current: number, size: number) => {
                setPageSize(size);
                setCurrentPage(1);
              },
              showQuickJumper: true,
              showLessItems: false,
              hideOnSinglePage: false,
              onChange: (page: number) => {
                setCurrentPage(page);
              },
              itemRender: (_page: number, type: string, originalElement: React.ReactNode) => {
                if (type === "prev") {
                  return (
                    <Button
                      icon={<LeftOutlined />}
                      disabled={currentPage === 1}
                      onClick={handlePrevPage}
                    >
                      Previous
                    </Button>
                  );
                }
                if (type === "next") {
                  return (
                    <Button
                      icon={<RightOutlined />}
                      disabled={!hasMoreData}
                      onClick={handleNextPage}
                    >
                      Next
                    </Button>
                  );
                }
                return originalElement;
              },
            }}
            scroll={{ x: 600 }}
          />
          </HorizontalScrollContainer>
        </Form>
      </Col>
    </Row>
  );

  const customerBySalespersonTable = (
    <Row style={{ marginTop: 8 }}>
      <Col span={24}>
        <Form form={form} component={false}>
          <HorizontalScrollContainer>
            <Table
              loading={loading}
              size="middle"
              showHeader={filteredCustomerBySalespersonData.length > 0}
              dataSource={filteredCustomerBySalespersonData.map((item, index) => ({
                key: `${item.customer_id}-${item.seller_id}-${index}`,
                ...item,
              }))}
              columns={customerBySalespersonColumns}
              rowClassName="editable-row"
              pagination={{
              position: ["bottomRight"],
              pageSize: pageSize,
              current: currentPage,
              total: total,
              showSizeChanger: true,
              pageSizeOptions: ["20", "50", "100", "200"],
              showTotal: (total: number, range: [number, number]) => 
                `Showing ${range[0]}-${range[1]} of ${total} records`,
              onShowSizeChange: (_current: number, size: number) => {
                setPageSize(size);
                setCurrentPage(1);
              },
              showQuickJumper: true,
              showLessItems: false,
              hideOnSinglePage: false,
              onChange: (page: number) => {
                setCurrentPage(page);
              },
              itemRender: (_page: number, type: string, originalElement: React.ReactNode) => {
                if (type === "prev") {
                  return (
                    <Button
                      icon={<LeftOutlined />}
                      disabled={currentPage === 1}
                      onClick={handlePrevPage}
                    >
                      Previous
                    </Button>
                  );
                }
                if (type === "next") {
                  return (
                    <Button
                      icon={<RightOutlined />}
                      disabled={!hasMoreData}
                      onClick={handleNextPage}
                    >
                      Next
                    </Button>
                  );
                }
                return originalElement;
              },
            }}
            scroll={{ x: 800 }}
          />
          </HorizontalScrollContainer>
        </Form>
      </Col>
    </Row>
  );

  const tabItems = [
    {
      key: "detail",
      label: "Detail Report",
      children: detailTable,
    },
    {
      key: "saleBySalesperson",
      label: "Sale by Seller",
      children: saleBySalespersonTable,
    },
    {
      key: "customerBySalesperson",
      label: "Customer by Seller",
      children: customerBySalespersonTable,
    },
  ];

  return (
    <React.Fragment>
      {control}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
      />
    </React.Fragment>
  );
};

export default withClinic(DailySaleDetailReportPage);


